import React, { useState } from "react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ActorTabs } from "@/components/ActorTabs";
import { GroupTabs } from "@/components/GroupTabs";
import { Doc } from "../../../convex/_generated/dataModel";
import {
  normalizeActorId,
  getSchemaDefaults,
  ApifyActor,
  ApifySchema,
} from "@/lib/apify";
import {
  Play,
  Plus,
  Copy,
  Terminal,
  CheckCircle2,
  Settings2,
  Trash2,
  Bug,
  Globe,
  Database,
  Zap,
  Search as SearchIcon,
  Loader2,
  ArrowLeft,
  ExternalLink,
  Square,
  Folder,
  FolderOpen,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { Id } from "../../../convex/_generated/dataModel";

const StatusDot = ({ status }: { status: string }) => {
  const color =
    status === "Healthy"
      ? "bg-emerald-500"
      : status === "Degraded"
        ? "bg-yellow-500"
        : "bg-red-500";

  return <div className={cn("w-2 h-2 rounded-full", color)} />;
};

interface ApifyActorSelectorProps {
  onSelect: (
    actor: ApifyActor,
    input: Record<string, unknown>,
    schema?: ApifySchema
  ) => void;
  onBack: () => void;
}

const ApifyActorSelector = ({ onSelect, onBack }: ApifyActorSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [actors, setActors] = useState<ApifyActor[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualActorId, setManualActorId] = useState("");
  const [isLoadingActor, setIsLoadingActor] = useState(false);

  const searchActors = useAction(api.apify.searchActors);
  const getActorInputSchema = useAction(api.apify.getActorInputSchema);
  const getActorDetail = useAction(api.apify.getActorDetail);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const results = await searchActors({ query: searchQuery, limit: 20 });
      setActors(results as ApifyActor[]);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleActorSelect = async (actor: ApifyActor) => {
    setIsLoadingSchema(true);
    try {
      const actorId = normalizeActorId(actor.id, actor);
      const schema = (await getActorInputSchema({ actorId })) as ApifySchema;

      let input: Record<string, unknown> = {};
      if (schema?.properties && Object.keys(schema.properties).length > 0) {
        input = getSchemaDefaults(schema);
      }

      // Automatically call onSelect with the loaded schema and defaults
      onSelect(actor, input, schema ?? undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      console.error("Failed to load schema:", error);
      // On error, still call onSelect with empty input
      onSelect(actor, {}, undefined);
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const handleLoadManualActor = async () => {
    if (!manualActorId.trim()) return;
    setIsLoadingActor(true);
    try {
      // Normalize actor ID (accept username~name or username/name)
      const normalizedId = manualActorId.trim().replace(/\//g, "~");
      const actorData = await getActorDetail({ actorId: normalizedId });

      // Convert to ApifyActor format
      const actor: ApifyActor = {
        id: normalizedId,
        name: actorData.name || normalizedId.split("~")[1] || "",
        username: actorData.username || normalizedId.split("~")[0] || "",
        title: actorData.title || actorData.name || normalizedId,
        description: actorData.description || "",
        stats: {
          totalRuns: actorData.stats?.totalRuns || 0,
          totalUsers: actorData.stats?.totalUsers || 0,
        },
        pictureUrl: actorData.pictureUrl,
      };

      // Automatically load schema and call onSelect
      setIsLoadingSchema(true);
      try {
        const schema = (await getActorInputSchema({
          actorId: normalizedId,
        })) as ApifySchema;
        let input: Record<string, unknown> = {};
        if (schema?.properties && Object.keys(schema.properties).length > 0) {
          input = getSchemaDefaults(schema);
        }
        onSelect(actor, input, schema ?? undefined);
      } catch (error) {
        console.error("Failed to load schema:", error);
        onSelect(actor, {}, undefined);
      } finally {
        setIsLoadingSchema(false);
      }

      setShowManualEntry(false);
      setManualActorId("");
    } catch (error: any) {
      alert(
        `Failed to load actor: ${error.message}\n\nMake sure:\n- The actor ID is correct (format: username~actor-name)\n- Your Apify token has access to this actor\n- The actor exists`
      );
    } finally {
      setIsLoadingActor(false);
    }
  };

  if (showManualEntry) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowManualEntry(false)}
            className="h-8 w-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h3 className="font-semibold">Enter Actor ID Manually</h3>
        </div>

        <div className="space-y-2">
          <Label>Actor ID</Label>
          <Input
            placeholder="username~actor-name (e.g., myusername~my-scraper)"
            value={manualActorId}
            onChange={(e) => setManualActorId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLoadManualActor()}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            Enter your actor ID in the format:{" "}
            <code className="bg-secondary px-1 rounded">
              username~actor-name
            </code>
            <br />
            You can also use{" "}
            <code className="bg-secondary px-1 rounded">
              username/actor-name
            </code>{" "}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowManualEntry(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleLoadManualActor}
            disabled={isLoadingActor || !manualActorId.trim()}
            className="flex-1"
          >
            {isLoadingActor ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Load Actor
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-8 w-8"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h3 className="font-semibold">Browse Apify Marketplace</h3>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search actors (e.g., 'google scraper', 'linkedin')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
        >
          {isSearching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <SearchIcon className="w-4 h-4" />
          )}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 border-t border-border"></div>
        <span className="text-xs text-muted-foreground">OR</span>
        <div className="flex-1 border-t border-border"></div>
      </div>

      <Button
        variant="outline"
        onClick={() => setShowManualEntry(true)}
        className="w-full"
      >
        <Database className="w-4 h-4 mr-2" />
        Enter Private Actor ID
      </Button>

      {actors.length > 0 ? (
        <div className="border border-border rounded-md max-h-[400px] overflow-y-auto">
          {actors.map((actor) => (
            <button
              key={actor.id}
              onClick={() => handleActorSelect(actor)}
              className="w-full p-4 text-left border-b border-border last:border-b-0 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {actor.pictureUrl && (
                  <img
                    src={actor.pictureUrl}
                    alt=""
                    className="w-10 h-10 rounded object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">
                    {actor.title || actor.name}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {actor.username}/{actor.name}
                  </div>
                  {actor.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {actor.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    {actor.stats && (
                      <>
                        <span className="text-xs text-muted-foreground">
                          {actor.stats.totalRuns?.toLocaleString() || 0} runs
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {actor.stats.totalUsers?.toLocaleString() || 0} users
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="border border-border rounded-md p-12 text-center">
          <Database className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            {isSearching
              ? "Searching..."
              : "Search for actors in the Apify marketplace"}
          </p>
        </div>
      )}
    </div>
  );
};

function formatLogTimestamp(ts: number | undefined): string {
  if (ts == null || isNaN(ts)) return "--:--:--";
  try {
    return format(new Date(ts), "HH:mm:ss");
  } catch {
    return "--:--:--";
  }
}

export const LogViewer = ({ logs }: { logs: any[] }) => {
  const [filter, setFilter] = useState<string>("ALL");
  const [expandedApifyLogs, setExpandedApifyLogs] = useState<Set<string>>(
    new Set()
  );

  // Sort logs chronologically (oldest first) for better readability
  const sortedLogs = [...logs].sort(
    (a, b) => (a.timestamp || 0) - (b.timestamp || 0)
  );

  const filteredLogs =
    filter === "ALL"
      ? sortedLogs
      : sortedLogs.filter((l) => l.level === filter);

  const copyToClipboard = () => {
    const text = logs
      .map(
        (l) =>
          `[${l.timestamp ?? ""}] [${l.level}] ${l.message} ${l.context ? JSON.stringify(l.context) : ""}`
      )
      .join("\n");
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex flex-col h-full border border-border rounded-sm bg-[#0d0d0d] text-zinc-300 font-mono text-xs">
      <div className="flex items-center justify-between p-2 border-b border-zinc-800 bg-[#1a1b26]/50">
        <div className="flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-zinc-400">/var/logs/scraper_v1.log</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-900 rounded-sm p-0.5 border border-zinc-800">
            {["ALL", "INFO", "WARN", "ERROR"].map((lvl) => (
              <button
                key={lvl}
                onClick={() => setFilter(lvl)}
                className={cn(
                  "px-2 py-0.5 rounded-sm transition-colors",
                  filter === lvl
                    ? "bg-zinc-700 text-white"
                    : "text-zinc-500 hover:text-zinc-300"
                )}
              >
                {lvl}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-zinc-500 hover:text-white hover:bg-zinc-800"
            onClick={copyToClipboard}
            title="Copy for LLM Troubleshooting"
          >
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1 font-mono custom-scrollbar">
        {filteredLogs.map((log) => {
          const hasApifyLogs =
            log.message.includes("Apify actor execution logs") &&
            log.context?.logSnippet;
          const hasFilterReasons =
            log.message.includes("items filtered out") &&
            log.context?.filterReasons;
          const isExpanded = expandedApifyLogs.has(log._id);

          return (
            <div key={log._id}>
              <div
                className={cn(
                  "flex gap-3 hover:bg-white/5 p-0.5 rounded -mx-0.5 group",
                  (hasApifyLogs || hasFilterReasons) && "cursor-pointer"
                )}
                onClick={() => {
                  if (hasApifyLogs || hasFilterReasons) {
                    setExpandedApifyLogs((prev) => {
                      const next = new Set(prev);
                      if (isExpanded) {
                        next.delete(log._id);
                      } else {
                        next.add(log._id);
                      }
                      return next;
                    });
                  }
                }}
              >
                <span className="text-zinc-600 shrink-0 select-none w-[100px]">
                  {formatLogTimestamp(log.timestamp)}
                </span>
                <span
                  className={cn(
                    "shrink-0 w-[50px] font-bold select-none",
                    log.level === "INFO"
                      ? "text-blue-400"
                      : log.level === "WARN"
                        ? "text-yellow-400"
                        : log.level === "ERROR"
                          ? "text-red-400"
                          : "text-zinc-500"
                  )}
                >
                  {log.level}
                </span>
                <span className="text-zinc-300 break-all flex-1">
                  {log.message}
                  {hasApifyLogs && (
                    <span className="text-zinc-500 text-[10px] ml-2">
                      (click to {isExpanded ? "hide" : "show"} Apify logs)
                    </span>
                  )}
                  {hasFilterReasons && (
                    <span className="text-zinc-500 text-[10px] ml-2">
                      (click to {isExpanded ? "hide" : "show"} filter reasons)
                    </span>
                  )}
                </span>
                {log.context && !hasApifyLogs && !hasFilterReasons && (
                  <span className="text-zinc-600 text-[10px] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity max-w-[200px] truncate">
                    {String(JSON.stringify(log.context))}
                  </span>
                )}
              </div>
              {hasApifyLogs && isExpanded && log.context?.logSnippet && (
                <div className="ml-[160px] mb-2 mt-1 p-2 bg-zinc-900/50 border border-zinc-800 rounded text-[10px] text-zinc-400 max-h-64 overflow-y-auto">
                  <div className="text-zinc-500 mb-1 text-[9px]">
                    Apify Actor Logs ({log.context.logLength} chars):
                  </div>
                  <pre className="whitespace-pre-wrap font-mono">
                    {log.context.logSnippet}
                  </pre>
                </div>
              )}
              {hasFilterReasons && isExpanded && log.context && (
                <div className="ml-[160px] mb-2 mt-1 p-2 bg-zinc-900/50 border border-zinc-800 rounded text-[10px] text-zinc-400">
                  <div className="text-zinc-500 mb-2 text-[9px] font-semibold">
                    Filter Reasons:
                  </div>
                  {log.context.filterReasons && (
                    <div className="mb-3">
                      <div className="text-zinc-500 mb-1 text-[9px]">
                        Counts:
                      </div>
                      <div className="space-y-1">
                        {Object.entries(log.context.filterReasons).map(
                          ([reason, count]) => (
                            <div
                              key={reason}
                              className="flex items-center gap-2"
                            >
                              <span className="text-yellow-400 font-semibold">
                                {String(count)}
                              </span>
                              <span className="text-zinc-400">{reason}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                  {log.context.sampleFiltered &&
                    Array.isArray(log.context.sampleFiltered) &&
                    log.context.sampleFiltered.length > 0 && (
                      <div>
                        <div className="text-zinc-500 mb-1 text-[9px]">
                          Sample Filtered Jobs:
                        </div>
                        <div className="space-y-2">
                          {log.context.sampleFiltered.map(
                            (item: any, idx: number) => (
                              <div
                                key={idx}
                                className="bg-zinc-950/50 p-2 rounded border border-zinc-800"
                              >
                                <div className="text-zinc-300 mb-1">
                                  <span className="font-semibold">
                                    {item.job?.title || "Unknown"}
                                  </span>
                                  <span className="text-zinc-500 ml-2">
                                    at {item.job?.company || "Unknown"}
                                  </span>
                                </div>
                                {item.reasons &&
                                  Array.isArray(item.reasons) &&
                                  item.reasons.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {item.reasons.map(
                                        (reason: string, rIdx: number) => (
                                          <span
                                            key={rIdx}
                                            className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-[9px]"
                                          >
                                            {reason}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          );
        })}
        {filteredLogs.length === 0 && (
          <div className="text-zinc-600 py-4 text-center">No logs yet</div>
        )}
        <div className="animate-pulse text-zinc-600 pt-2 flex items-center gap-2">
          <span className="text-emerald-500">➜</span> Listening for new
          events...
        </div>
      </div>
    </div>
  );
};

type ScraperSource = Doc<"scraperSources">;
type SourceGroup = Doc<"sourceGroups">;
type LogEntry = Doc<"logs">;

export default function ScraperStatus() {
  const sources = useQuery(api.sources.list) || [];
  const groups = useQuery(api.sources.listGroups) || [];
  const jobs = useQuery(api.jobs.list, {}) || [];
  // @ts-expect-error - run action exists and is exported in source.ts
  const runSource = useAction(api.sources.run);
  // @ts-expect-error - runGroup action exists and is exported in source.ts
  const runGroup = useAction(api.sources.runGroup);
  // @ts-expect-error - checkRunStatuses action exists
  const checkRunStatuses = useAction(api.sources.checkRunStatuses);
  const abortRun = useAction(api.apify.abortRun);
  const getRunStatus = useAction(api.apify.getRunStatus);
  const getActorRuns = useAction(api.apify.getActorRuns);
  const getActorInputSchema = useAction(api.apify.getActorInputSchema);
  const deleteSource = useMutation(api.sources.remove);
  const createSource = useMutation(api.sources.create);
  const createGroup = useMutation(api.sources.createGroup);
  const updateGroup = useMutation(api.sources.updateGroup);
  const updateSource = useMutation(api.sources.update);
  const abortGroupRuns = useAction(api.runs.abortGroupRuns);
  const getRunningRunsForGroup = useAction(api.runs.getRunningRunsForGroup);
  const deleteGroup = useMutation(api.sources.removeGroup);
  const { toast } = useToast();

  const [selectedSource, setSelectedSource] = useState<ScraperSource | null>(
    null
  );
  const [selectedGroup, setSelectedGroup] = useState<SourceGroup | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<Id<"sourceGroups">>>(
    new Set()
  );
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedSourceIds, setSelectedSourceIds] = useState<
    Id<"scraperSources">[]
  >([]);
  const [sourceName, setSourceName] = useState("");
  const [selectedApifyActor, setSelectedApifyActor] =
    useState<ApifyActor | null>(null);
  const [apifyActorInput, setApifyActorInput] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [apifyActorSchema, setApifyActorSchema] = useState<ApifySchema | null>(
    null
  );
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedGroupRunId, setSelectedGroupRunId] = useState<string | null>(
    null
  );

  const logs =
    (useQuery(
      api.logs.listBySource,
      selectedSource?._id ? { sourceId: selectedSource._id } : "skip"
    ) as LogEntry[] | undefined) ?? [];

  // Get current run for selected source
  const currentRun = useQuery(
    api.runs.getBySourceId,
    selectedSource?._id ? { sourceId: selectedSource._id } : "skip"
  ) as Doc<"runs"> | undefined | null;

  // Auto-select current run when it changes (new run started)
  React.useEffect(() => {
    if (currentRun && currentRun.status === "RUNNING") {
      // Only auto-select if no run is currently selected or if the current run is different
      if (!selectedRunId || selectedRunId !== currentRun.runId) {
        setSelectedRunId(currentRun.runId);
      }
    }
  }, [currentRun?.runId, currentRun?.status, selectedRunId]);

  // Reset selected run when source changes
  React.useEffect(() => {
    setSelectedRunId(null);
  }, [selectedSource?._id]);

  // Get all running group runs
  const runningGroupRuns = useQuery(api.runs.listRunningByGroup) as
    | Doc<"runs">[]
    | undefined;

  // Get running runs for selected group (if any)
  const selectedGroupRunningRuns = useQuery(
    api.runs.listRunningByGroupId,
    selectedGroup?._id ? { groupId: selectedGroup._id } : "skip"
  ) as Doc<"runs">[] | undefined;

  // Get current run for selected group (most recent running run)
  const currentGroupRun = useQuery(
    api.runs.getByGroupId,
    selectedGroup?._id ? { groupId: selectedGroup._id } : "skip"
  ) as Doc<"runs"> | undefined | null;

  // Auto-select current group run when it changes (new run started)
  React.useEffect(() => {
    if (currentGroupRun && currentGroupRun.status === "RUNNING") {
      // Only auto-select if no run is currently selected or if the current run is different
      if (!selectedGroupRunId || selectedGroupRunId !== currentGroupRun.runId) {
        setSelectedGroupRunId(currentGroupRun.runId);
      }
    }
  }, [currentGroupRun?.runId, currentGroupRun?.status, selectedGroupRunId]);

  // Reset selected group run when group changes
  React.useEffect(() => {
    setSelectedGroupRunId(null);
  }, [selectedGroup?._id]);

  // Derive running status from run document
  const isRunning = currentRun?.status === "RUNNING";
  const currentRunId = currentRun?.runId ?? null;

  // Helper to check if a specific group is running
  const isGroupRunning = React.useCallback(
    (groupId: Id<"sourceGroups"> | string | null) => {
      if (!groupId || !runningGroupRuns) return false;
      const groupIdStr = groupId.toString();
      return runningGroupRuns.some(
        (run) => run.groupId?.toString() === groupIdStr
      );
    },
    [runningGroupRuns]
  );

  // Helper to get source status from logs
  const getSourceRunStatus = React.useCallback(
    (
      logs: LogEntry[],
      groupId?: string,
      currentRunningIndex?: number,
      sourceIndex?: number
    ) => {
      if (!logs || logs.length === 0) return "idle";

      // Check recent logs (last 50 to catch group runs)
      const recentLogs = logs.slice(0, 50);

      // If groupId is provided, we're tracking a group run
      // Check for logs with group trigger in the source's own logs
      if (groupId) {
        // Find the most recent group run by looking for group-triggered logs
        const groupTriggeredLogs = recentLogs.filter((log) =>
          log.context?.trigger?.startsWith("group:")
        );

        if (groupTriggeredLogs.length === 0) {
          // No group logs yet - check if this source should be running
          if (
            currentRunningIndex !== undefined &&
            sourceIndex === currentRunningIndex
          ) {
            return "running";
          }
          // If sources before this one are running, this one is pending
          if (
            currentRunningIndex !== undefined &&
            sourceIndex !== undefined &&
            sourceIndex > currentRunningIndex
          ) {
            return "idle";
          }
          return "idle";
        }

        // Find the most recent run start
        const runStartLog = groupTriggeredLogs.find(
          (log) =>
            log.message.includes("Run started") ||
            log.message.includes("Apify run started")
        );

        if (!runStartLog) {
          // Check if this source should be running based on currentRunningIndex
          if (
            currentRunningIndex !== undefined &&
            sourceIndex === currentRunningIndex
          ) {
            return "running";
          }
          return "idle";
        }

        const runStartTime = runStartLog.timestamp || 0;

        // Check logs after this run started
        const runLogs = groupTriggeredLogs.filter(
          (log) => (log.timestamp || 0) >= runStartTime
        );

        const hasCompleted = runLogs.some(
          (log) =>
            log.message.includes("Run completed successfully") ||
            log.message.includes("Processing complete")
        );

        const hasFailed = runLogs.some(
          (log) =>
            log.message.includes("Run failed") ||
            log.message.includes("Run ended with status")
        );

        // If completed or failed, always show that status
        if (hasCompleted) return "completed";
        if (hasFailed) return "failed";

        // If not completed/failed, check if this is the currently running source
        if (currentRunningIndex !== undefined && sourceIndex !== undefined) {
          if (sourceIndex === currentRunningIndex) {
            return "running";
          }
          // If a source after this one is running, this one should be completed
          // But we already checked for completion above, so if we get here it might be idle
          if (sourceIndex < currentRunningIndex) {
            // This source should have completed by now, but logs might not be updated yet
            // Return idle as a fallback
            return "idle";
          }
          // If a source before this one is running, this one hasn't started
          return "idle";
        }

        // Fallback: if we found a start log but no completion, it might be running
        // But only if we can't determine the current running index
        return "idle";
      }

      // For non-group runs, check regular source logs
      const hasStarted = recentLogs.some(
        (log) =>
          log.message.includes("Run started") ||
          log.message.includes("Apify run started")
      );

      const hasCompleted = recentLogs.some(
        (log) =>
          log.message.includes("Run completed successfully") ||
          log.message.includes("Processing complete")
      );

      const hasFailed = recentLogs.some(
        (log) =>
          log.message.includes("Run failed") ||
          log.message.includes("Run ended with status")
      );

      if (hasCompleted) return "completed";
      if (hasFailed) return "failed";
      if (hasStarted) {
        // Check if it's still running (started but not completed/failed)
        const startTime =
          recentLogs.find(
            (log) =>
              log.message.includes("Run started") ||
              log.message.includes("Apify run started")
          )?.timestamp || 0;

        const laterLogs = recentLogs.filter(
          (log) => (log.timestamp || 0) > startTime
        );
        const hasEnded = laterLogs.some(
          (log) =>
            log.message.includes("completed") || log.message.includes("failed")
        );

        return hasEnded ? "idle" : "running";
      }

      return "idle";
    },
    []
  );

  React.useEffect(() => {
    // Only auto-select first source if no source AND no group is selected
    if (!selectedSource && !selectedGroup && sources.length > 0) {
      setSelectedSource(sources[0]);
    }
  }, [sources, selectedSource, selectedGroup]);

  // Auto-expand group when a source from it is selected
  React.useEffect(() => {
    if (selectedSource?.groupId) {
      setExpandedGroups((prev) => {
        const newExpanded = new Set(prev);
        newExpanded.add(selectedSource.groupId!);
        return newExpanded;
      });
    }
  }, [selectedSource?.groupId]);

  // Check run statuses on-demand when sources are loaded (only if there are sources with currentRunId)
  React.useEffect(() => {
    const hasActiveRuns = sources.some((s) => s.currentRunId);
    if (hasActiveRuns) {
      // Check run statuses when page loads or sources change
      checkRunStatuses({}).catch((error) => {
        console.error("Failed to check run statuses:", error);
      });
    }
  }, [sources, checkRunStatuses]);

  const handleApifyActorSelect = async (
    actor: ApifyActor,
    input: Record<string, unknown>,
    schema?: ApifySchema
  ) => {
    setSelectedApifyActor(actor);
    // Auto-fill source name if empty
    if (!sourceName.trim()) {
      setSourceName(actor.title || actor.name);
    }

    // If schema is already provided, use it
    if (schema) {
      setApifyActorSchema(schema);
      setApifyActorInput(input);
      return;
    }

    // Otherwise, automatically load the schema and fill defaults
    setIsLoadingSchema(true);
    try {
      const actorId = normalizeActorId(actor.id, actor);
      const loadedSchema = (await getActorInputSchema({
        actorId,
      })) as ApifySchema;
      setApifyActorSchema(loadedSchema);

      if (
        loadedSchema?.properties &&
        Object.keys(loadedSchema.properties).length > 0
      ) {
        const defaults = getSchemaDefaults(loadedSchema);
        setApifyActorInput(defaults);
      } else {
        // No schema available, use empty object
        setApifyActorInput({});
      }
    } catch (error) {
      console.error("Failed to load schema:", error);
      // On error, use the provided input or empty object
      setApifyActorInput(input || {});
      setApifyActorSchema(null);
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const resetDialog = () => {
    setIsAddSourceOpen(false);
    setSourceName("");
    setSelectedApifyActor(null);
    setApifyActorInput(null);
    setApifyActorSchema(null);
  };

  const handleCreateSource = async () => {
    try {
      // Validate required fields
      if (!sourceName.trim()) {
        toast({
          title: "Name required",
          description: "Please enter a name for this source",
          variant: "destructive",
        });
        return;
      }

      if (!selectedApifyActor) {
        toast({
          title: "Actor required",
          description: "Please select an Apify actor",
          variant: "destructive",
        });
        return;
      }

      const normalizedActorId = normalizeActorId(
        selectedApifyActor.id,
        selectedApifyActor
      );

      await createSource({
        name: sourceName.trim(),
        type: "apify",
        url: "",
        schedule: "0 */6 * * *",
        config: {
          actorId: normalizedActorId,
          actorName: selectedApifyActor.title || selectedApifyActor.name,
          input: apifyActorInput ?? {},
          inputSchema: apifyActorSchema ?? undefined,
        },
        enabled: true,
      });

      toast({
        title: "Source created",
        description: `${sourceName} has been added successfully`,
      });

      resetDialog();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to create source",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleRunNow = async () => {
    if (!selectedSource) return;

    // Prevent multiple clicks - check database state
    if (isRunning) {
      toast({
        title: "Run in progress",
        description: "A run is already in progress for this source.",
        variant: "destructive",
      });
      return;
    }

    // Prevent running if source's group is currently running
    if (selectedSource.groupId && isGroupRunning(selectedSource.groupId)) {
      toast({
        title: "Group is running",
        description:
          "This source's group is currently running. Please wait for it to complete.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await runSource({
        id: selectedSource._id,
        trigger: "manual",
      });

      const description =
        result?.newJobs !== undefined
          ? `Processed ${result.newJobs} new jobs${result.duplicates ? `, ${result.duplicates} duplicates skipped` : ""}`
          : result?.runId
            ? `Apify run ${result.runId} started`
            : `${selectedSource.name} is now running`;
      toast({
        title: "Scraper started",
        description,
      });
      // State is now managed in the database via currentRunId
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to start scraper",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleDebug = () => {
    if (!selectedSource) return;
    toast({
      title: "Debug Mode",
      description: "Debug functionality will be implemented soon",
    });
  };

  const handleDeleteSource = async () => {
    if (!selectedSource) return;

    if (
      !confirm(
        `Are you sure you want to delete "${selectedSource.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteSource({ id: selectedSource._id });
      toast({
        title: "Source deleted",
        description: `${selectedSource.name} has been removed`,
      });
      setSelectedSource(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to delete source",
        description: message,
        variant: "destructive",
      });
    }
  };

  // Group handlers
  const handleCreateGroup = async () => {
    try {
      if (!groupName.trim()) {
        toast({
          title: "Name required",
          description: "Please enter a name for this group",
          variant: "destructive",
        });
        return;
      }

      if (selectedSourceIds.length === 0) {
        toast({
          title: "Sources required",
          description: "Please select at least one source for this group",
          variant: "destructive",
        });
        return;
      }

      await createGroup({
        name: groupName.trim(),
        sourceIds: selectedSourceIds,
        enabled: true,
      });

      toast({
        title: "Group created",
        description: `${groupName} has been created with ${selectedSourceIds.length} source(s)`,
      });

      setIsAddGroupOpen(false);
      setGroupName("");
      setSelectedSourceIds([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to create group",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleEditGroup = async () => {
    if (!selectedGroup) return;

    try {
      if (!groupName.trim()) {
        toast({
          title: "Name required",
          description: "Please enter a name for this group",
          variant: "destructive",
        });
        return;
      }

      if (selectedSourceIds.length === 0) {
        toast({
          title: "Sources required",
          description: "Please select at least one source for this group",
          variant: "destructive",
        });
        return;
      }

      await updateGroup({
        id: selectedGroup._id,
        name: groupName.trim(),
        sourceIds: selectedSourceIds,
      });

      toast({
        title: "Group updated",
        description: `${groupName} has been updated`,
      });

      setIsEditGroupOpen(false);
      setGroupName("");
      setSelectedSourceIds([]);
      setSelectedGroup(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to update group",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteGroup = async (groupId: Id<"sourceGroups">) => {
    if (
      !confirm(
        "Are you sure you want to delete this group? Sources will be removed from the group but not deleted."
      )
    ) {
      return;
    }

    try {
      await deleteGroup({ id: groupId });
      toast({
        title: "Group deleted",
        description: "Group has been removed",
      });
      if (selectedGroup?._id === groupId) {
        setSelectedGroup(null);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to delete group",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleRunGroup = async (groupId: Id<"sourceGroups">) => {
    // Prevent multiple clicks - check database state
    if (isGroupRunning(groupId)) {
      toast({
        title: "Group run in progress",
        description: "This group is already running.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await runGroup({
        id: groupId,
        trigger: "manual",
      });

      const description =
        result?.totalNewJobs !== undefined
          ? `Processed ${result.totalNewJobs} new jobs from ${result.successCount}/${result.sourceCount} sources`
          : "Group run started";

      toast({
        title: "Group run started",
        description,
      });
      // State is now managed in the database via currentRunId
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to start group run",
        description: message,
        variant: "destructive",
      });
    }
  };

  const openEditGroup = (group: SourceGroup) => {
    setSelectedGroup(group);
    setGroupName(group.name);
    setSelectedSourceIds(group.sourceIds);
    setIsEditGroupOpen(true);
  };

  const handleAbortGroup = async (groupId: Id<"sourceGroups">) => {
    const group = groups.find((g) => g._id === groupId);
    if (!group) return;

    try {
      // abortGroupRuns now handles everything: finding runs, aborting Apify runs,
      // updating database, and clearing currentRunId from all sources
      const abortedRunCount = await abortGroupRuns({ groupId });
      console.log(
        `[handleAbortGroup] Aborted ${abortedRunCount} run(s) and cleared currentRunId`
      );

      if (abortedRunCount > 0) {
        toast({
          title: "Group runs aborted",
          description: `Aborted ${abortedRunCount} active run${abortedRunCount !== 1 ? "s" : ""}`,
        });
      } else {
        toast({
          title: "Group runs cleared",
          description: "All running indicators have been cleared",
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to abort group runs",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleAbortRun = async () => {
    if (!selectedSource) return;

    try {
      let runIdToAbort: string | null = null;

      // First, try to use the stored runId from current run
      if (currentRunId) {
        // Check if the run is still active
        try {
          const status = await getRunStatus({ runId: currentRunId });
          if (status.status === "RUNNING" || status.status === "READY") {
            runIdToAbort = currentRunId;
          }
        } catch (error) {
          // Run might not exist or be accessible, try other methods
        }
      }

      // If no active runId from state, check for active runs via API
      if (!runIdToAbort && selectedSource.config?.actorId) {
        try {
          const actorRuns = await getActorRuns({
            actorId: selectedSource.config.actorId,
            limit: 5,
          });
          const activeRun = actorRuns.find(
            (run: any) => run.status === "RUNNING" || run.status === "READY"
          );
          if (activeRun) {
            runIdToAbort = activeRun.id;
          }
        } catch (error) {
          // Fall back to logs
        }
      }

      // Fallback: Get the most recent log entry with a runId
      if (!runIdToAbort) {
        const sourceLogs = logs.filter((log) => log.runId);
        if (sourceLogs.length > 0) {
          const mostRecentLog = sourceLogs[0];
          if (mostRecentLog.runId) {
            // Check if this run is still active
            try {
              const status = await getRunStatus({ runId: mostRecentLog.runId });
              if (status.status === "RUNNING" || status.status === "READY") {
                runIdToAbort = mostRecentLog.runId;
              }
            } catch (error) {
              // Run is likely already done
            }
          }
        }
      }

      if (!runIdToAbort) {
        toast({
          title: "No active run found",
          description: "There is no active run to abort for this source",
          variant: "destructive",
        });
        return;
      }

      await abortRun({ runId: runIdToAbort });
      toast({
        title: "Run aborted",
        description: `Aborted run ${runIdToAbort}`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to abort run",
        description: message,
        variant: "destructive",
      });
    }
  };

  const totalJobs = jobs.length;
  const avgSuccessRate =
    sources.length > 0
      ? (
          sources.reduce((acc, s) => acc + (s.successRate || 0), 0) /
          sources.length
        ).toFixed(1)
      : 0;
  const avgLatency =
    sources.length > 0
      ? (
          sources.reduce((acc, s) => acc + (s.avgLatency || 0), 0) /
          sources.length /
          1000
        ).toFixed(1)
      : 0;
  const activeSources = sources.filter((s) => s.enabled).length;

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      {/* Top Metrics Bar */}
      <div className="flex border-b border-border h-16 shrink-0 divide-x divide-border pl-6">
        <div className="flex-1 bg-card p-4 flex flex-col justify-center min-w-0">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
            Indexed Jobs
          </span>
          <div className="text-xl font-bold font-mono flex items-baseline gap-2">
            {totalJobs.toLocaleString()}
          </div>
        </div>
        <div className="flex-1 bg-card p-4 flex flex-col justify-center min-w-0">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
            Success Rate
          </span>
          <div className="text-xl font-bold font-mono flex items-baseline gap-2">
            {avgSuccessRate}%
          </div>
        </div>
        <div className="flex-1 bg-card p-4 flex flex-col justify-center min-w-0">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
            Avg Latency
          </span>
          <div className="text-xl font-bold font-mono text-foreground">
            {avgLatency}s
          </div>
        </div>
        <div className="flex-1 bg-card p-4 flex flex-col justify-center min-w-0">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
            Active Sources
          </span>
          <div className="text-xl font-bold font-mono flex items-center gap-2">
            {activeSources} / {sources.length}
          </div>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Source List */}
        <div className="w-[280px] border-r border-border bg-card flex flex-col">
          <div className="h-14 px-6 border-b border-border flex items-center justify-between bg-secondary/10">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Sources
            </h3>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-background hover:shadow-sm border-black/50"
                onClick={() => setIsAddGroupOpen(true)}
                title="Add Group"
              >
                <Folder className="w-4 h-4 text-black" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-background hover:shadow-sm border-black/50"
                onClick={() => setIsAddSourceOpen(true)}
                title="Add Source"
              >
                <Plus className="w-4 h-4 text-black" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {groups.length === 0 && sources.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No sources configured. Add your first source to start scraping.
              </div>
            ) : (
              <>
                {/* Groups */}
                {groups.map((group) => {
                  const groupSources = sources.filter((s) =>
                    group.sourceIds.includes(s._id)
                  );
                  // Check if group is expanded
                  const isExpanded = expandedGroups.has(group._id);
                  // Check if group is selected
                  const isGroupSelected = selectedGroup?._id === group._id;
                  return (
                    <div key={group._id} className="space-y-1">
                      <div
                        className={cn(
                          "w-full flex items-center justify-between p-3 rounded-sm border transition-all group cursor-pointer",
                          isGroupSelected
                            ? "bg-background border-border shadow-sm"
                            : "bg-transparent border-transparent hover:bg-secondary/50 hover:border-border/50"
                        )}
                        onClick={(e) => {
                          // If clicking on action buttons, don't select the group
                          if ((e.target as HTMLElement).closest("button")) {
                            return;
                          }
                          // Select the group and expand it
                          setSelectedGroup(group);
                          setSelectedSource(null);
                          const newExpanded = new Set(expandedGroups);
                          newExpanded.add(group._id);
                          setExpandedGroups(newExpanded);
                        }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {isExpanded ? (
                            <FolderOpen className="w-4 h-4 text-blue-500 shrink-0" />
                          ) : (
                            <Folder className="w-4 h-4 text-blue-500 shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium group-hover:text-primary transition-colors truncate">
                              {group.name}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {group.sourceIds.length} source
                              {group.sourceIds.length !== 1 ? "s" : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunGroup(group._id);
                            }}
                            disabled={isGroupRunning(group._id)}
                            title="Run Group"
                          >
                            {isGroupRunning(group._id) ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                          </Button>
                          {isGroupRunning(group._id) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAbortGroup(group._id);
                              }}
                              title="Abort Group"
                            >
                              <Square className="w-3 h-3" />
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              asChild
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                              >
                                <Settings2 className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => openEditGroup(group)}
                              >
                                Edit Group
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleDeleteGroup(group._id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete Group
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      {/* Group sources (indented) */}
                      {isExpanded &&
                        groupSources.map((source) => (
                          <button
                            key={source._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSource(source);
                              setSelectedGroup(null);
                            }}
                            className={cn(
                              "w-full flex items-center justify-between p-2 pl-8 rounded-sm border transition-all group ml-2 text-left",
                              selectedSource?._id === source._id
                                ? "bg-background border-border shadow-sm"
                                : "bg-transparent border-transparent hover:bg-secondary/50 hover:border-border/50"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <StatusDot status={source.status} />
                              <div>
                                <div className="text-xs font-medium group-hover:text-primary transition-colors">
                                  {source.name}
                                </div>
                                <div className="text-[9px] text-muted-foreground font-mono">
                                  {source.schedule || "Manual"}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                    </div>
                  );
                })}
                {/* Ungrouped sources */}
                {sources
                  .filter((source) => !source.groupId)
                  .map((source) => (
                    <button
                      key={source._id}
                      onClick={() => {
                        setSelectedSource(source);
                        setSelectedGroup(null);

                        // Expand the group if source belongs to one, collapse others
                        const newExpanded = new Set(expandedGroups);
                        if (source.groupId) {
                          // Expand the group this source belongs to
                          newExpanded.add(source.groupId);
                          // Collapse other groups
                          groups.forEach((g) => {
                            if (g._id !== source.groupId) {
                              newExpanded.delete(g._id);
                            }
                          });
                        } else {
                          // If source is ungrouped, collapse all groups
                          groups.forEach((g) => {
                            newExpanded.delete(g._id);
                          });
                        }
                        setExpandedGroups(newExpanded);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-sm text-left border transition-all group",
                        selectedSource?._id === source._id
                          ? "bg-background border-border shadow-sm"
                          : "bg-transparent border-transparent hover:bg-secondary/50 hover:border-border/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <StatusDot status={source.status} />
                        <div>
                          <div className="text-sm font-medium group-hover:text-primary transition-colors">
                            {source.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {source.schedule || "Manual"}
                          </div>
                        </div>
                      </div>
                      <div className="text-right opacity-60 group-hover:opacity-100">
                        <div className="text-[10px] font-mono">
                          {source.successRate || 0}%
                        </div>
                      </div>
                    </button>
                  ))}
              </>
            )}
          </div>

          <div className="p-2 border-t border-border bg-secondary/10">
            <div className="text-[10px] text-center text-muted-foreground font-mono">
              Engine: v2.4.1 (Stable)
            </div>
          </div>
        </div>

        {/* Right Panel: Source Details & Logs */}
        <div className="flex-1 flex flex-col bg-background min-w-0">
          {!selectedSource && !selectedGroup ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              {sources.length === 0 && groups.length === 0
                ? "No sources configured yet"
                : "Select a source or group to view details"}
            </div>
          ) : selectedGroup ? (
            <>
              {/* Group Toolbar */}
              <div className="h-14 border-b border-border flex items-center justify-between px-6 shrink-0 bg-card/50">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-blue-500" />
                    <h2 className="text-lg font-bold tracking-tight">
                      {selectedGroup.name}
                    </h2>
                  </div>
                  <Badge
                    variant="outline"
                    className="font-mono text-[10px] px-2 py-0.5 border-0 uppercase tracking-wider bg-blue-500/10 text-blue-600"
                  >
                    {selectedGroup.sourceIds.length} source
                    {selectedGroup.sourceIds.length !== 1 ? "s" : ""}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-8 text-xs gap-2 shadow-sm"
                    onClick={() => handleRunGroup(selectedGroup._id)}
                    disabled={isGroupRunning(selectedGroup._id)}
                  >
                    {isGroupRunning(selectedGroup._id) ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                        Running...
                      </>
                    ) : (
                      <>
                        <Play className="w-3.5 h-3.5" /> Run Group
                      </>
                    )}
                  </Button>
                  {isGroupRunning(selectedGroup._id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-2 text-red-600 border-red-600 hover:text-red-700 hover:border-red-700"
                      onClick={() => handleAbortGroup(selectedGroup._id)}
                    >
                      <Square className="w-3.5 h-3.5" /> Abort
                    </Button>
                  )}
                  <div className="w-px h-4 bg-border mx-1" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Settings2 className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onClick={() => openEditGroup(selectedGroup)}
                      >
                        Edit Group
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteGroup(selectedGroup._id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Group Content */}
              <GroupTabs
                selectedGroup={selectedGroup}
                selectedRunId={selectedGroupRunId}
                onRunSelect={setSelectedGroupRunId}
              />
            </>
          ) : (
            <>
              {/* Toolbar */}
              <div className="h-14 border-b border-border flex items-center justify-between px-6 shrink-0 bg-card/50">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <h2 className="text-lg font-bold tracking-tight">
                      {selectedSource.name}
                    </h2>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "font-mono text-[10px] px-2 py-0.5 border-0 uppercase tracking-wider",
                      selectedSource.status === "Healthy"
                        ? "bg-emerald-500/10 text-emerald-600"
                        : selectedSource.status === "Degraded"
                          ? "bg-yellow-500/10 text-yellow-600"
                          : "bg-red-500/10 text-red-600"
                    )}
                  >
                    {selectedSource.status}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-2"
                    onClick={handleDebug}
                  >
                    <Bug className="w-3.5 h-3.5" /> Debug
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 text-xs gap-2 shadow-sm"
                    onClick={handleRunNow}
                    disabled={
                      isRunning ||
                      (selectedSource.groupId &&
                        isGroupRunning(selectedSource.groupId))
                    }
                    title={
                      selectedSource.groupId &&
                      isGroupRunning(selectedSource.groupId)
                        ? "This source's group is currently running"
                        : undefined
                    }
                  >
                    {isRunning ||
                    (selectedSource.groupId &&
                      isGroupRunning(selectedSource.groupId)) ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                        Running...
                      </>
                    ) : (
                      <>
                        <Play className="w-3.5 h-3.5" /> Run Now
                      </>
                    )}
                  </Button>
                  {isRunning && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-2 text-red-600 border-red-600 hover:text-red-700 hover:border-red-700"
                      onClick={handleAbortRun}
                    >
                      <Square className="w-3.5 h-3.5" /> Abort
                    </Button>
                  )}
                  <div className="w-px h-4 bg-border mx-1" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Settings2 className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onClick={() =>
                          toast({
                            title: "Coming soon",
                            description:
                              "Source editing will be available soon",
                          })
                        }
                      >
                        Edit Configuration
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          toast({
                            title: "Coming soon",
                            description:
                              "API key management will be available soon",
                          })
                        }
                      >
                        Manage API Keys
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          toast({
                            title: "Coming soon",
                            description: "Run history will be available soon",
                          })
                        }
                      >
                        View History
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={handleDeleteSource}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete Source
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Main Content Tabs */}
              <ActorTabs
                selectedSource={selectedSource}
                logs={logs}
                selectedRunId={selectedRunId}
                onRunSelect={setSelectedRunId}
              />
            </>
          )}
        </div>
      </div>

      {/* Add Source Dialog - Apify Actor Only */}
      <Dialog open={isAddSourceOpen} onOpenChange={resetDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Apify Actor Source</DialogTitle>
            <DialogDescription>
              Select an Apify actor from the marketplace to use as a scraper
              source.
            </DialogDescription>
          </DialogHeader>

          {selectedApifyActor ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedApifyActor(null)}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="flex-1">
                  <h3 className="font-semibold">
                    {selectedApifyActor.title || selectedApifyActor.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {selectedApifyActor.username}/{selectedApifyActor.name}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Source Name</Label>
                <Input
                  placeholder="e.g. HackerNews Jobs"
                  value={sourceName}
                  onChange={(e) => setSourceName(e.target.value)}
                />
              </div>

              {isLoadingSchema ? (
                <div className="border border-border rounded-md p-8 text-center">
                  <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Loading actor configuration...
                  </p>
                </div>
              ) : (
                <>
                  {apifyActorSchema?.properties &&
                    Object.keys(apifyActorSchema.properties).length > 0 && (
                      <div className="border border-border rounded-md p-4 bg-secondary/20 max-h-[300px] overflow-y-auto">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold">
                            Available Filters & Options
                          </h4>
                          <Badge variant="outline" className="text-xs">
                            {Object.keys(apifyActorSchema.properties).length}{" "}
                            parameters available
                          </Badge>
                        </div>
                        <div className="space-y-3">
                          {Object.entries(apifyActorSchema.properties).map(
                            ([key, prop]: [string, any]) => (
                              <div
                                key={key}
                                className="text-xs border-b border-border pb-2 last:border-b-0"
                              >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <code className="font-mono font-semibold text-foreground">
                                    {key}
                                  </code>
                                  <div className="flex items-center gap-1">
                                    {apifyActorSchema.required?.includes(
                                      key
                                    ) && (
                                      <Badge
                                        variant="destructive"
                                        className="text-[10px] px-1 py-0"
                                      >
                                        Required
                                      </Badge>
                                    )}
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1 py-0"
                                    >
                                      {prop.type || "any"}
                                    </Badge>
                                  </div>
                                </div>
                                {prop.title && (
                                  <div className="text-muted-foreground mb-1">
                                    {prop.title}
                                  </div>
                                )}
                                {prop.description && (
                                  <div className="text-muted-foreground">
                                    {prop.description}
                                  </div>
                                )}
                                {prop.default !== undefined && (
                                  <div className="text-muted-foreground mt-1">
                                    Default:{" "}
                                    <code className="font-mono text-xs bg-secondary px-1 rounded">
                                      {JSON.stringify(prop.default)}
                                    </code>
                                  </div>
                                )}
                                {prop.enum && (
                                  <div className="text-muted-foreground mt-1">
                                    Options:{" "}
                                    {prop.enum.map((v: any) => (
                                      <code
                                        key={v}
                                        className="font-mono text-xs bg-secondary px-1 rounded mr-1"
                                      >
                                        {JSON.stringify(v)}
                                      </code>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  <div className="border border-border rounded-md p-4 bg-secondary/20">
                    <div className="space-y-2">
                      <Label className="text-xs">
                        Actor Input Configuration
                      </Label>
                      <Textarea
                        value={
                          apifyActorInput
                            ? JSON.stringify(apifyActorInput, null, 2)
                            : "{}"
                        }
                        onChange={(e) => {
                          try {
                            setApifyActorInput(JSON.parse(e.target.value));
                          } catch (err) {
                            // Keep as string if invalid JSON
                          }
                        }}
                        className="font-mono text-xs min-h-[200px]"
                      />
                    </div>
                  </div>
                </>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSelectedApifyActor(null)}
                >
                  Back
                </Button>
                <Button
                  onClick={handleCreateSource}
                  disabled={
                    !sourceName.trim() ||
                    !selectedApifyActor ||
                    isLoadingSchema ||
                    !apifyActorInput
                  }
                >
                  <Zap className="w-4 h-4 mr-2" /> Create Source
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <ApifyActorSelector
              onSelect={handleApifyActorSelect}
              onBack={resetDialog}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={isAddGroupOpen} onOpenChange={setIsAddGroupOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Source Group</DialogTitle>
            <DialogDescription className="mt-2">
              Group sources together to run them sequentially, one after
              another.
            </DialogDescription>
            <DialogDescription>
              Each Source can also be accessed and ran independently from the
              group.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                placeholder="e.g. LinkedIn Sources"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Select Sources</Label>
              <div className="border border-border rounded-md p-3 max-h-[300px] overflow-y-auto space-y-2">
                {sources.filter((s) => !s.groupId).length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No ungrouped sources available. All sources are already in
                    groups.
                  </div>
                ) : (
                  sources
                    .filter((s) => !s.groupId)
                    .map((source) => (
                      <div
                        key={source._id}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-secondary/50"
                      >
                        <Checkbox
                          id={`source-${source._id}`}
                          checked={selectedSourceIds.includes(source._id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSourceIds([
                                ...selectedSourceIds,
                                source._id,
                              ]);
                            } else {
                              setSelectedSourceIds(
                                selectedSourceIds.filter(
                                  (id) => id !== source._id
                                )
                              );
                            }
                          }}
                        />
                        <label
                          htmlFor={`source-${source._id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                        >
                          {source.name}
                        </label>
                        <StatusDot status={source.status} />
                      </div>
                    ))
                )}
              </div>
              {selectedSourceIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedSourceIds.length} source
                  {selectedSourceIds.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditGroupOpen(false);
                setGroupName("");
                setSelectedSourceIds([]);
                setSelectedGroup(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || selectedSourceIds.length === 0}
            >
              <Folder className="w-4 h-4 mr-2" /> Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={isEditGroupOpen} onOpenChange={setIsEditGroupOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Source Group</DialogTitle>
            <DialogDescription>
              Update the group name and source membership.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input
                placeholder="e.g. LinkedIn Sources"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Select Sources</Label>
              <div className="border border-border rounded-md p-3 max-h-[300px] overflow-y-auto space-y-2">
                {sources.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No sources available.
                  </div>
                ) : (
                  sources.map((source) => {
                    // Check if source is in the group being edited
                    const editingGroup = groups.find(
                      (g) => g._id === selectedGroup?._id
                    );
                    const isInGroup =
                      editingGroup?.sourceIds.includes(source._id) || false;
                    const isInOtherGroup =
                      source.groupId && source.groupId !== selectedGroup?._id;
                    return (
                      <div
                        key={source._id}
                        className={cn(
                          "flex items-center space-x-2 p-2 rounded",
                          isInOtherGroup
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-secondary/50"
                        )}
                      >
                        <Checkbox
                          id={`edit-source-${source._id}`}
                          checked={selectedSourceIds.includes(source._id)}
                          disabled={isInOtherGroup}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSourceIds([
                                ...selectedSourceIds,
                                source._id,
                              ]);
                            } else {
                              setSelectedSourceIds(
                                selectedSourceIds.filter(
                                  (id) => id !== source._id
                                )
                              );
                            }
                          }}
                        />
                        <label
                          htmlFor={`edit-source-${source._id}`}
                          className={cn(
                            "text-sm font-medium leading-none cursor-pointer flex-1",
                            isInOtherGroup && "cursor-not-allowed"
                          )}
                        >
                          {source.name}
                          {isInOtherGroup && (
                            <span className="text-xs text-muted-foreground ml-2">
                              (in another group)
                            </span>
                          )}
                        </label>
                        <StatusDot status={source.status} />
                      </div>
                    );
                  })
                )}
              </div>
              {selectedSourceIds.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedSourceIds.length} source
                  {selectedSourceIds.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditGroupOpen(false);
                setGroupName("");
                setGroupTelegramChannelId("");
                setSelectedSourceIds([]);
                setSelectedGroup(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditGroup}
              disabled={!groupName.trim() || selectedSourceIds.length === 0}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
